import { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../hooks/useTheme';
import MagneticButton from '../../../components/base/MagneticButton';
import TypeWriter from '../../../components/base/TypeWriter';
import FloatingElement from '../../../components/base/FloatingElement';
import AnimatedCounter from '../../../components/base/AnimatedCounter';

export default function HeroSection() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { t, i18n } = useTranslation();
  const { isDark } = useTheme();
  const [isLoaded, setIsLoaded] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [logoClicked, setLogoClicked] = useState(false);
  const [ripples, setRipples] = useState<Array<{ id: number; x: number; y: number }>>([]);
  const [activeNodes, setActiveNodes] = useState<Set<number>>(new Set());
  const [chainReaction, setChainReaction] = useState<Array<{ from: number; to: number; id: number }>>([]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    setMousePos({
      x: (e.clientX / window.innerWidth - 0.5) * 2,
      y: (e.clientY / window.innerHeight - 0.5) * 2
    });
  }, []);

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [handleMouseMove]);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  // 节点位置数据
  const nodePositions = [
    { x: 15, y: 25, size: 8, delay: 0, connections: [4, 6] },
    { x: 85, y: 20, size: 6, delay: 0.5, connections: [5, 6] },
    { x: 10, y: 70, size: 7, delay: 1, connections: [4, 7] },
    { x: 90, y: 75, size: 8, delay: 1.5, connections: [5, 7] },
    { x: 25, y: 50, size: 5, delay: 2, connections: [0, 2] },
    { x: 75, y: 45, size: 6, delay: 2.5, connections: [1, 3] },
    { x: 50, y: 15, size: 7, delay: 3, connections: [0, 1] },
    { x: 50, y: 85, size: 6, delay: 3.5, connections: [2, 3] },
  ];

  // 处理节点点击
  const handleNodeClick = useCallback((nodeIndex: number) => {
    // 激活当前节点
    setActiveNodes(prev => new Set([...prev, nodeIndex]));
    
    // 创建连锁反应
    const node = nodePositions[nodeIndex];
    const newReactions: Array<{ from: number; to: number; id: number }> = [];
    
    node.connections.forEach((targetIndex, i) => {
      setTimeout(() => {
        const reactionId = Date.now() + i;
        newReactions.push({ from: nodeIndex, to: targetIndex, id: reactionId });
        setChainReaction(prev => [...prev, { from: nodeIndex, to: targetIndex, id: reactionId }]);
        
        // 激活目标节点
        setTimeout(() => {
          setActiveNodes(prev => new Set([...prev, targetIndex]));
          
          // 二级连锁反应
          const targetNode = nodePositions[targetIndex];
          targetNode.connections.forEach((secondaryIndex, j) => {
            if (secondaryIndex !== nodeIndex) {
              setTimeout(() => {
                const secondaryId = Date.now() + 100 + j;
                setChainReaction(prev => [...prev, { from: targetIndex, to: secondaryIndex, id: secondaryId }]);
                
                setTimeout(() => {
                  setActiveNodes(prev => new Set([...prev, secondaryIndex]));
                }, 300);
                
                // 清除二级连锁动画
                setTimeout(() => {
                  setChainReaction(prev => prev.filter(r => r.id !== secondaryId));
                }, 600);
              }, 200 * j);
            }
          });
        }, 300);
        
        // 清除连锁动画
        setTimeout(() => {
          setChainReaction(prev => prev.filter(r => r.id !== reactionId));
        }, 600);
      }, 150 * i);
    });
    
    // 重置所有节点状态
    setTimeout(() => {
      setActiveNodes(new Set());
    }, 2000);
  }, []);

  // 3D 粒子星球效果
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();

    // 创建粒子
    const particles: Array<{
      x: number;
      y: number;
      z: number;
      baseX: number;
      baseY: number;
      radius: number;
      color: string;
      speed: number;
      angle: number;
    }> = [];

    const centerX = canvas.width * 0.7;
    const centerY = canvas.height * 0.5;
    const sphereRadius = Math.min(canvas.width, canvas.height) * 0.35;

    // 球体粒子
    for (let i = 0; i < 600; i++) {
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      const r = sphereRadius * (0.8 + Math.random() * 0.2);
      
      particles.push({
        x: centerX + r * Math.sin(phi) * Math.cos(theta),
        y: centerY + r * Math.sin(phi) * Math.sin(theta),
        z: r * Math.cos(phi),
        baseX: r * Math.sin(phi) * Math.cos(theta),
        baseY: r * Math.sin(phi) * Math.sin(theta),
        radius: Math.random() * 2 + 0.5,
        color: Math.random() > 0.7 ? '#F06030' : '#E84A1F',
        speed: 0.002 + Math.random() * 0.003,
        angle: theta
      });
    }

    // 环绕粒子
    for (let i = 0; i < 150; i++) {
      const angle = (i / 150) * Math.PI * 2;
      const orbitRadius = sphereRadius * (1.2 + Math.random() * 0.3);
      
      particles.push({
        x: centerX + orbitRadius * Math.cos(angle),
        y: centerY + orbitRadius * Math.sin(angle) * 0.3,
        z: orbitRadius * Math.sin(angle),
        baseX: orbitRadius * Math.cos(angle),
        baseY: orbitRadius * Math.sin(angle) * 0.3,
        radius: Math.random() * 1.5 + 0.5,
        color: '#E84A1F',
        speed: 0.008,
        angle: angle
      });
    }

    let rotation = 0;

    function animate() {
      if (!ctx || !canvas) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      rotation += 0.003;

      // 绘制粒子
      particles.forEach((p, i) => {
        // 旋转变换
        const cos = Math.cos(rotation + p.speed * i);
        const sin = Math.sin(rotation + p.speed * i);
        
        const rotatedX = p.baseX * cos - p.z * sin;
        const rotatedZ = p.baseX * sin + p.z * cos;
        
        p.x = centerX + rotatedX;
        const depth = (rotatedZ + sphereRadius) / (sphereRadius * 2);
        const scale = 0.5 + depth * 0.5;
        
        // 深度透明度
        const alpha = 0.2 + depth * 0.8;
        
        // 发光效果
        const gradient = ctx.createRadialGradient(
          p.x, p.y, 0,
          p.x, p.y, p.radius * scale * 4
        );
        gradient.addColorStop(0, p.color.replace(')', `, ${alpha})`).replace('rgb', 'rgba').replace('#E84A1F', `rgba(232, 74, 31, ${alpha})`).replace('#F06030', `rgba(240, 96, 48, ${alpha})`));
        gradient.addColorStop(1, 'rgba(232, 74, 31, 0)');
        
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius * scale * 4, 0, Math.PI * 2);
        ctx.fillStyle = gradient;
        ctx.fill();

        // 核心点
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius * scale, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(232, 74, 31, ${alpha})`;
        ctx.fill();
      });

      // 绘制连接线
      particles.slice(0, 200).forEach((p1, i) => {
        particles.slice(i + 1, 200).forEach((p2) => {
          const dx = p1.x - p2.x;
          const dy = p1.y - p2.y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < 80) {
            ctx.beginPath();
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.strokeStyle = `rgba(232, 74, 31, ${0.1 * (1 - dist / 80)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        });
      });

      requestAnimationFrame(animate);
    }

    animate();

    window.addEventListener('resize', resizeCanvas);
    return () => window.removeEventListener('resize', resizeCanvas);
  }, []);

  const handleExploreMore = () => {
    const nextSection = document.querySelector('#core-capabilities');
    if (nextSection) {
      nextSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleLogoClick = () => {
    setLogoClicked(true);
    
    // 添加波纹效果
    const newRipple = { id: Date.now(), x: 50, y: 50 };
    setRipples(prev => [...prev, newRipple]);
    
    // 移除波纹
    setTimeout(() => {
      setRipples(prev => prev.filter(r => r.id !== newRipple.id));
    }, 1000);
    
    // 重置点击状态
    setTimeout(() => setLogoClicked(false), 600);
  };

  return (
    <section className={`relative min-h-screen overflow-hidden transition-colors duration-500 ${isDark ? 'bg-[#0a0a0a]' : 'bg-[#fafafa]'}`}>
      {/* 动态背景渐变 */}
      <div 
        className="absolute inset-0 transition-transform duration-300 ease-out"
        style={{
          transform: `translate(${mousePos.x * 20}px, ${mousePos.y * 20}px)`
        }}
      >
        <div className={`absolute top-[-20%] left-[-10%] w-[300px] sm:w-[400px] md:w-[600px] h-[300px] sm:h-[400px] md:h-[600px] rounded-full blur-[80px] md:blur-[120px] ${isDark ? 'bg-[#E84A1F]/20' : 'bg-[#E84A1F]/10'}`}></div>
        <div className={`absolute bottom-[-20%] right-[20%] w-[250px] sm:w-[350px] md:w-[500px] h-[250px] sm:h-[350px] md:h-[500px] rounded-full blur-[60px] md:blur-[100px] ${isDark ? 'bg-[#F06030]/15' : 'bg-[#F06030]/8'}`}></div>
        <div className={`absolute top-[30%] right-[-10%] w-[200px] sm:w-[300px] md:w-[400px] h-[200px] sm:h-[300px] md:h-[400px] rounded-full blur-[60px] md:blur-[80px] ${isDark ? 'bg-[#E84A1F]/10' : 'bg-[#E84A1F]/5'}`}></div>
      </div>

      {/* 网格背景 */}
      <div 
        className={`absolute inset-0 ${isDark ? 'opacity-[0.03]' : 'opacity-[0.02]'}`}
        style={{
          backgroundImage: `linear-gradient(#E84A1F 1px, transparent 1px), linear-gradient(90deg, #E84A1F 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }}
      ></div>

      {/* 动态光束 - 移动端隐藏 */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none hidden md:block">
        <div className={`absolute top-0 left-[20%] w-[2px] h-full bg-gradient-to-b from-transparent via-[#E84A1F]/30 to-transparent animate-[beam_8s_ease-in-out_infinite]`}></div>
        <div className={`absolute top-0 left-[50%] w-[1px] h-full bg-gradient-to-b from-transparent via-[#E84A1F]/20 to-transparent animate-[beam_12s_ease-in-out_infinite_2s]`}></div>
        <div className={`absolute top-0 left-[80%] w-[2px] h-full bg-gradient-to-b from-transparent via-[#F06030]/25 to-transparent animate-[beam_10s_ease-in-out_infinite_4s]`}></div>
      </div>

      {/* 3D 粒子画布 - 移动端降低透明度 */}
      <canvas ref={canvasRef} className={`absolute inset-0 ${isDark ? 'opacity-60 md:opacity-80' : 'opacity-30 md:opacity-50'}`} />

      {/* 主内容区 */}
      <div className="relative z-10 min-h-screen flex items-center pt-14 sm:pt-16 md:pt-20">
        <div className="w-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8 items-center">
            
            {/* 左侧：错落式大字排版 */}
            <div className="lg:col-span-7 space-y-3 sm:space-y-4">
              {/* 错落式主标题 */}
              <div className="space-y-1 sm:space-y-2">
                <div className={`overflow-hidden transition-all duration-1000 delay-200 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}>
                  <h1 
                    className={`text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-black tracking-tight leading-[0.9] transition-transform duration-1000 ${isLoaded ? 'translate-y-0' : 'translate-y-full'}`}
                    style={{ transitionDelay: '200ms' }}
                  >
                    <span className={`inline-block ${isDark ? 'text-white' : 'text-[#0a0a0a]'}`}>BUILD</span>
                  </h1>
                </div>
                
                <div className={`overflow-hidden transition-all duration-1000 delay-300 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}>
                  <h1 
                    className={`text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-black tracking-tight leading-[0.9] ml-4 sm:ml-8 md:ml-16 transition-transform duration-1000 ${isLoaded ? 'translate-y-0' : 'translate-y-full'}`}
                    style={{ transitionDelay: '400ms' }}
                  >
                    <span className="inline-block bg-gradient-to-r from-[#E84A1F] via-[#F06030] to-[#E84A1F] bg-clip-text text-transparent bg-[length:200%_auto] animate-[gradient_3s_linear_infinite]">
                      THE FUTURE
                    </span>
                  </h1>
                </div>
                
                <div className={`overflow-hidden transition-all duration-1000 delay-400 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}>
                  <h1 
                    className={`text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-black tracking-tight leading-[0.9] transition-transform duration-1000 ${isLoaded ? 'translate-y-0' : 'translate-y-full'}`}
                    style={{ transitionDelay: '600ms' }}
                  >
                    <span className={`inline-block ${isDark ? 'text-transparent bg-clip-text' : 'text-transparent bg-clip-text'}`} style={{
                      WebkitTextStroke: isDark ? '1.5px rgba(255,255,255,0.3)' : '1.5px rgba(10,10,10,0.2)'
                    }}>OF RWA</span>
                  </h1>
                </div>
              </div>

              {/* 描述文字 */}
              <div className={`max-w-lg transition-all duration-1000 delay-700 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                <p className={`text-sm sm:text-base md:text-lg leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {t('hero.subtitle')}
                </p>
                <div className={`mt-2 text-xs sm:text-sm ${isDark ? 'text-[#E84A1F]' : 'text-[#E84A1F]'}`}>
                  <TypeWriter 
                    texts={['Real Estate Tokenization', 'Energy Asset Trading', 'Commodity Finance', 'Structured Products']}
                    typingSpeed={80}
                    deletingSpeed={40}
                    pauseDuration={2500}
                    cursorClassName="text-[#E84A1F]"
                  />
                </div>
              </div>

              {/* CTA 按钮组 */}
              <div className={`flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 pt-2 transition-all duration-1000 delay-800 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                <MagneticButton 
                  onClick={handleExploreMore}
                  intensity={0.3}
                  scale={1.05}
                  className="group relative px-6 sm:px-8 py-3 sm:py-4 bg-[#E84A1F] rounded-full font-semibold text-white overflow-hidden transition-all duration-300 hover:shadow-[0_0_50px_rgba(232,74,31,0.5)] whitespace-nowrap text-sm sm:text-base text-center"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                  <span className="relative z-10 flex items-center justify-center gap-2">
                    {t('hero.cta.start')}
                    <i className="ri-arrow-right-up-line group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300"></i>
                  </span>
                </MagneticButton>
                
                <MagneticButton
                  as="a"
                  href={i18n.language === 'zh' ? 'https://t1.roonchain.com/RoonChain%20%E5%AE%98%E7%BD%91%E9%A6%96%E9%A1%B5%20%C2%B7%20%E4%B8%AD%E6%96%87%E6%AD%A3%E5%BC%8F%E7%89%88%E6%96%87%E6%A1%88.pdf' : 'https://t1.roonchain.com/RoonChain%20Whitepaper%20V1.0.pdf'}
                  target="_blank"
                  rel="noopener noreferrer"
                  intensity={0.3}
                  scale={1.05}
                  className={`group relative px-6 sm:px-8 py-3 sm:py-4 rounded-full font-semibold transition-all duration-300 whitespace-nowrap overflow-hidden text-sm sm:text-base text-center ${
                    isDark 
                      ? 'bg-white/5 text-white hover:bg-white/10 border border-white/10' 
                      : 'bg-black/5 text-[#0a0a0a] hover:bg-black/10 border border-black/10'
                  }`}
                >
                  <span className="relative z-10 flex items-center justify-center gap-2">
                    <i className="ri-file-text-line text-[#E84A1F]"></i>
                    {t('hero.cta.docs')}
                  </span>
                </MagneticButton>
              </div>

              {/* 底部数据条 */}
              <div className={`flex flex-wrap items-center gap-4 sm:gap-6 md:gap-8 pt-4 transition-all duration-1000 delay-1000 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                {[
                  { value: 10000, label: 'TPS', icon: 'ri-speed-line', prefix: '', suffix: '+' },
                  { value: 3, label: 'Finality', icon: 'ri-time-line', prefix: '<', suffix: 's' },
                  { value: 5.8, label: 'TVL', icon: 'ri-funds-line', prefix: '$', suffix: 'B', decimals: 1 },
                ].map((stat, index) => (
                  <div 
                    key={index} 
                    className="group flex items-center gap-2 sm:gap-3 cursor-default"
                    style={{ transitionDelay: `${1000 + index * 100}ms` }}
                  >
                    <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl flex items-center justify-center transition-all duration-300 group-hover:scale-110 group-hover:rotate-6 ${isDark ? 'bg-[#E84A1F]/10' : 'bg-[#E84A1F]/5'}`}>
                      <i className={`${stat.icon} text-sm sm:text-base text-[#E84A1F] group-hover:animate-pulse`}></i>
                    </div>
                    <div>
                      <div className={`text-base sm:text-lg md:text-xl font-bold ${isDark ? 'text-white' : 'text-[#0a0a0a]'} group-hover:text-[#E84A1F] transition-colors`}>
                        <AnimatedCounter 
                          end={stat.value} 
                          prefix={stat.prefix} 
                          suffix={stat.suffix} 
                          duration={2000}
                          decimals={stat.decimals || 0}
                        />
                      </div>
                      <div className={`text-[10px] sm:text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{stat.label}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 右侧：3D Logo 展示区 - 仅大屏幕显示 */}
            <div className="lg:col-span-5 relative hidden lg:block">
              <div 
                className="relative w-full aspect-square"
                style={{
                  transform: `perspective(1000px) rotateY(${mousePos.x * 10}deg) rotateX(${-mousePos.y * 10}deg)`
                }}
              >
                {/* 数据流动轨迹 - 外层 */}
                <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ overflow: 'visible' }}>
                  <defs>
                    <linearGradient id="dataFlowGradient1" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#E84A1F" stopOpacity="0" />
                      <stop offset="50%" stopColor="#E84A1F" stopOpacity="0.8" />
                      <stop offset="100%" stopColor="#E84A1F" stopOpacity="0" />
                    </linearGradient>
                    <linearGradient id="dataFlowGradient2" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#F06030" stopOpacity="0" />
                      <stop offset="50%" stopColor="#F06030" stopOpacity="0.6" />
                      <stop offset="100%" stopColor="#F06030" stopOpacity="0" />
                    </linearGradient>
                    <filter id="glow">
                      <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                      <feMerge>
                        <feMergeNode in="coloredBlur"/>
                        <feMergeNode in="SourceGraphic"/>
                      </feMerge>
                    </filter>
                  </defs>
                  
                  {/* 椭圆轨道 1 */}
                  <ellipse 
                    cx="50%" cy="50%" rx="42%" ry="20%" 
                    fill="none" 
                    stroke={isDark ? 'rgba(232,74,31,0.15)' : 'rgba(232,74,31,0.1)'} 
                    strokeWidth="1"
                    strokeDasharray="4 8"
                    className="animate-[spin_30s_linear_infinite]"
                    style={{ transformOrigin: '50% 50%' }}
                  />
                  
                  {/* 椭圆轨道 2 - 倾斜 */}
                  <ellipse 
                    cx="50%" cy="50%" rx="38%" ry="18%" 
                    fill="none" 
                    stroke={isDark ? 'rgba(240,96,48,0.12)' : 'rgba(240,96,48,0.08)'} 
                    strokeWidth="1"
                    strokeDasharray="6 10"
                    className="animate-[spin_25s_linear_infinite_reverse]"
                    style={{ transformOrigin: '50% 50%', transform: 'rotate(60deg)' }}
                  />
                  
                  {/* 椭圆轨道 3 - 另一个角度 */}
                  <ellipse 
                    cx="50%" cy="50%" rx="35%" ry="16%" 
                    fill="none" 
                    stroke={isDark ? 'rgba(232,74,31,0.1)' : 'rgba(232,74,31,0.06)'} 
                    strokeWidth="1"
                    strokeDasharray="3 6"
                    className="animate-[spin_20s_linear_infinite]"
                    style={{ transformOrigin: '50% 50%', transform: 'rotate(-45deg)' }}
                  />
                </svg>

                {/* 数据流粒子 - 沿轨道运动 */}
                <div className="absolute inset-0">
                  {[...Array(6)].map((_, i) => {
                    const delay = i * 1.5;
                    const duration = 8 + i * 2;
                    const orbitIndex = i % 3;
                    const rotations = [0, 60, -45];
                    return (
                      <div
                        key={`flow-${i}`}
                        className="absolute left-1/2 top-1/2 w-full h-full"
                        style={{
                          transform: `rotate(${rotations[orbitIndex]}deg)`,
                          animation: `orbit${orbitIndex + 1} ${duration}s linear infinite`,
                          animationDelay: `${delay}s`
                        }}
                      >
                        <div 
                          className="absolute w-3 h-3 rounded-full bg-[#E84A1F] shadow-[0_0_15px_#E84A1F,0_0_30px_#E84A1F]"
                          style={{
                            left: '92%',
                            top: '50%',
                            transform: 'translate(-50%, -50%)',
                            opacity: 0.9
                          }}
                        >
                          <div className="absolute inset-0 rounded-full bg-white/50 animate-ping"></div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* 区块链节点网络 */}
                <div className="absolute inset-0">
                  {/* 节点位置 */}
                  {nodePositions.map((node, i) => (
                    <div
                      key={`node-${i}`}
                      className={`absolute cursor-pointer transition-all duration-300 ${activeNodes.has(i) ? 'z-20' : 'z-10'}`}
                      style={{
                        left: `${node.x}%`,
                        top: `${node.y}%`,
                        transform: 'translate(-50%, -50%)'
                      }}
                      onClick={() => handleNodeClick(i)}
                    >
                      {/* 节点激活时的大光环 */}
                      {activeNodes.has(i) && (
                        <>
                          <div 
                            className="absolute rounded-full bg-[#E84A1F]/30 animate-[nodeActivePulse_0.6s_ease-out]"
                            style={{ 
                              width: node.size * 8, 
                              height: node.size * 8,
                              left: '50%',
                              top: '50%',
                              transform: 'translate(-50%, -50%)',
                            }}
                          ></div>
                          <div 
                            className="absolute rounded-full border-2 border-[#E84A1F] animate-[nodeActiveRing_0.8s_ease-out]"
                            style={{ 
                              width: node.size * 6, 
                              height: node.size * 6,
                              left: '50%',
                              top: '50%',
                              transform: 'translate(-50%, -50%)',
                            }}
                          ></div>
                        </>
                      )}
                      
                      {/* 节点脉冲光环 */}
                      <div 
                        className={`absolute inset-0 rounded-full transition-all duration-300 ${activeNodes.has(i) ? 'bg-[#E84A1F]/40' : 'bg-[#E84A1F]/20'} animate-[nodePulse_3s_ease-out_infinite]`}
                        style={{ 
                          width: node.size * 4, 
                          height: node.size * 4,
                          left: '50%',
                          top: '50%',
                          transform: 'translate(-50%, -50%)',
                          animationDelay: `${node.delay}s`
                        }}
                      ></div>
                      
                      {/* 节点核心 */}
                      <div 
                        className={`relative rounded-full transition-all duration-300 ${activeNodes.has(i) ? 'bg-gradient-to-br from-[#FF6B4A] to-[#E84A1F] scale-150 shadow-[0_0_20px_rgba(232,74,31,0.8)]' : 'bg-gradient-to-br from-[#E84A1F] to-[#F06030] shadow-[0_0_10px_rgba(232,74,31,0.5)]'}`}
                        style={{ width: node.size, height: node.size }}
                      >
                        <div className="absolute inset-[2px] rounded-full bg-white/30"></div>
                      </div>
                      
                      {/* 悬停提示 */}
                      <div className={`absolute -bottom-6 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded text-[8px] whitespace-nowrap transition-all duration-300 opacity-0 hover:opacity-100 ${isDark ? 'bg-white/10 text-white/60' : 'bg-black/5 text-black/40'}`}>
                        Node {i + 1}
                      </div>
                    </div>
                  ))}
                </div>

                {/* 连锁反应动画线条 */}
                <svg className="absolute inset-0 w-full h-full pointer-events-none z-30" style={{ overflow: 'visible' }}>
                  <defs>
                    <linearGradient id="chainReactionGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#E84A1F" stopOpacity="1" />
                      <stop offset="50%" stopColor="#FF6B4A" stopOpacity="1" />
                      <stop offset="100%" stopColor="#E84A1F" stopOpacity="0" />
                    </linearGradient>
                    <filter id="chainGlow">
                      <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                      <feMerge>
                        <feMergeNode in="coloredBlur"/>
                        <feMergeNode in="SourceGraphic"/>
                      </feMerge>
                    </filter>
                  </defs>
                  
                  {chainReaction.map((reaction) => {
                    const fromNode = nodePositions[reaction.from];
                    const toNode = nodePositions[reaction.to];
                    return (
                      <g key={reaction.id}>
                        {/* 发光线条 */}
                        <line
                          x1={`${fromNode.x}%`}
                          y1={`${fromNode.y}%`}
                          x2={`${toNode.x}%`}
                          y2={`${toNode.y}%`}
                          stroke="#E84A1F"
                          strokeWidth="3"
                          filter="url(#chainGlow)"
                          className="animate-[chainLine_0.6s_ease-out_forwards]"
                          style={{ opacity: 0 }}
                        />
                        {/* 移动的能量球 */}
                        <circle
                          r="6"
                          fill="#FF6B4A"
                          filter="url(#chainGlow)"
                          className="animate-[chainBall_0.5s_ease-out_forwards]"
                        >
                          <animateMotion
                            dur="0.5s"
                            fill="freeze"
                            path={`M${fromNode.x * 3.5},${fromNode.y * 3.5} L${toNode.x * 3.5},${toNode.y * 3.5}`}
                          />
                        </circle>
                      </g>
                    );
                  })}
                </svg>

                {/* 节点连接线 - SVG */}
                <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ overflow: 'visible' }}>
                  <defs>
                    <linearGradient id="lineGradientH" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#E84A1F" stopOpacity="0" />
                      <stop offset="50%" stopColor="#E84A1F" stopOpacity="0.4" />
                      <stop offset="100%" stopColor="#E84A1F" stopOpacity="0" />
                    </linearGradient>
                    <linearGradient id="lineGradientV" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor="#F06030" stopOpacity="0" />
                      <stop offset="50%" stopColor="#F06030" stopOpacity="0.3" />
                      <stop offset="100%" stopColor="#F06030" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  
                  {/* 连接线 - 动态 */}
                  <line x1="15%" y1="25%" x2="50%" y2="50%" stroke="url(#lineGradientH)" strokeWidth="1" className="animate-[lineFlash_4s_ease-in-out_infinite]" />
                  <line x1="85%" y1="20%" x2="50%" y2="50%" stroke="url(#lineGradientH)" strokeWidth="1" className="animate-[lineFlash_4s_ease-in-out_infinite_0.5s]" />
                  <line x1="10%" y1="70%" x2="50%" y2="50%" stroke="url(#lineGradientH)" strokeWidth="1" className="animate-[lineFlash_4s_ease-in-out_infinite_1s]" />
                  <line x1="90%" y1="75%" x2="50%" y2="50%" stroke="url(#lineGradientH)" strokeWidth="1" className="animate-[lineFlash_4s_ease-in-out_infinite_1.5s]" />
                  <line x1="25%" y1="50%" x2="50%" y2="50%" stroke="url(#lineGradientV)" strokeWidth="1" className="animate-[lineFlash_4s_ease-in-out_infinite_2s]" />
                  <line x1="75%" y1="45%" x2="50%" y2="50%" stroke="url(#lineGradientV)" strokeWidth="1" className="animate-[lineFlash_4s_ease-in-out_infinite_2.5s]" />
                  <line x1="50%" y1="15%" x2="85%" y2="20%" stroke="url(#lineGradientV)" strokeWidth="1" className="animate-[lineFlash_4s_ease-in-out_infinite_3s]" />
                  <line x1="50%" y1="85%" x2="90%" y2="75%" stroke="url(#lineGradientV)" strokeWidth="1" className="animate-[lineFlash_4s_ease-in-out_infinite_3.5s]" />
                  
                  {/* 节点间连接 */}
                  <line x1="15%" y1="25%" x2="25%" y2="50%" stroke={isDark ? 'rgba(232,74,31,0.15)' : 'rgba(232,74,31,0.1)'} strokeWidth="0.5" strokeDasharray="3 5" />
                  <line x1="85%" y1="20%" x2="75%" y2="45%" stroke={isDark ? 'rgba(232,74,31,0.15)' : 'rgba(232,74,31,0.1)'} strokeWidth="0.5" strokeDasharray="3 5" />
                  <line x1="10%" y1="70%" x2="25%" y2="50%" stroke={isDark ? 'rgba(240,96,48,0.12)' : 'rgba(240,96,48,0.08)'} strokeWidth="0.5" strokeDasharray="3 5" />
                  <line x1="90%" y1="75%" x2="75%" y2="45%" stroke={isDark ? 'rgba(240,96,48,0.12)' : 'rgba(240,96,48,0.08)'} strokeWidth="0.5" strokeDasharray="3 5" />
                  <line x1="50%" y1="15%" x2="85%" y2="20%" stroke={isDark ? 'rgba(232,74,31,0.1)' : 'rgba(232,74,31,0.06)'} strokeWidth="0.5" strokeDasharray="3 5" />
                  <line x1="50%" y1="85%" x2="90%" y2="75%" stroke={isDark ? 'rgba(232,74,31,0.1)' : 'rgba(232,74,31,0.06)'} strokeWidth="0.5" strokeDasharray="3 5" />
                </svg>

                {/* 數據傳輸動畫 - 沿連接線移動的光點 */}
                <div className="absolute inset-0">
                  {[
                    { from: { x: 15, y: 25 }, to: { x: 50, y: 50 }, delay: 0, duration: 2 },
                    { from: { x: 85, y: 20 }, to: { x: 50, y: 50 }, delay: 0.8, duration: 2.2 },
                    { from: { x: 10, y: 70 }, to: { x: 50, y: 50 }, delay: 1.6, duration: 1.8 },
                    { from: { x: 90, y: 75 }, to: { x: 50, y: 50 }, delay: 2.4, duration: 2 },
                  ].map((path, i) => (
                    <div
                      key={`data-${i}`}
                      className="absolute w-2 h-2 rounded-full bg-[#E84A1F] shadow-[0_0_8px_#E84A1F]"
                      style={{
                        left: `${path.from.x}%`,
                        top: `${path.from.y}%`,
                        animation: `dataTransfer${i} ${path.duration}s ease-in-out infinite`,
                        animationDelay: `${path.delay}s`,
                        opacity: 0
                      }}
                    ></div>
                  ))}
                </div>

                {/* RWA 資產浮動圖標 */}
                <div className="absolute inset-0">
                  {[
                    { icon: 'ri-building-4-line', label: 'Real Estate', x: 8, y: 40, delay: 0, size: 40 },
                    { icon: 'ri-flashlight-line', label: 'Energy', x: 92, y: 35, delay: 0.8, size: 36 },
                    { icon: 'ri-coin-line', label: 'Gold', x: 12, y: 85, delay: 1.6, size: 34 },
                    { icon: 'ri-file-text-line', label: 'Bonds', x: 88, y: 88, delay: 2.4, size: 38 },
                    { icon: 'ri-plant-line', label: 'Agriculture', x: 5, y: 12, delay: 3.2, size: 32 },
                    { icon: 'ri-ship-line', label: 'Commodities', x: 95, y: 8, delay: 4, size: 36 },
                  ].map((asset, i) => (
                    <div
                      key={`asset-${i}`}
                      className="absolute group pointer-events-auto cursor-pointer"
                      style={{
                        left: `${asset.x}%`,
                        top: `${asset.y}%`,
                        transform: 'translate(-50%, -50%)',
                        animation: `assetFloat ${4 + i * 0.5}s ease-in-out infinite`,
                        animationDelay: `${asset.delay}s`
                      }}
                    >
                      {/* 圖標背景光暈 */}
                      <div 
                        className={`absolute inset-[-8px] rounded-xl blur-md transition-all duration-500 group-hover:inset-[-16px] group-hover:blur-xl ${isDark ? 'bg-[#E84A1F]/20 group-hover:bg-[#E84A1F]/40' : 'bg-[#E84A1F]/15 group-hover:bg-[#E84A1F]/30'}`}
                        style={{ animation: `assetGlow 3s ease-in-out infinite`, animationDelay: `${asset.delay}s` }}
                      ></div>
                      
                      {/* 懸停時的脈衝環 */}
                      <div className="absolute inset-[-4px] rounded-xl border-2 border-[#E84A1F]/0 group-hover:border-[#E84A1F]/60 group-hover:animate-[assetPulseRing_1s_ease-out_infinite] transition-all duration-300"></div>
                      <div className="absolute inset-[-8px] rounded-xl border border-[#E84A1F]/0 group-hover:border-[#E84A1F]/40 group-hover:animate-[assetPulseRing_1s_ease-out_infinite_0.3s] transition-all duration-300"></div>
                      
                      {/* 圖標容器 */}
                      <div 
                        className={`relative flex items-center justify-center rounded-xl backdrop-blur-sm border transition-all duration-300 group-hover:scale-125 group-hover:rotate-6 group-hover:border-[#E84A1F]/60 ${
                          isDark 
                            ? 'bg-[#1a1a1a]/80 border-[#E84A1F]/30 shadow-[0_4px_20px_rgba(232,74,31,0.2)] group-hover:bg-[#1a1a1a] group-hover:shadow-[0_8px_40px_rgba(232,74,31,0.5)]' 
                            : 'bg-white/90 border-[#E84A1F]/20 shadow-[0_4px_20px_rgba(232,74,31,0.15)] group-hover:bg-white group-hover:shadow-[0_8px_40px_rgba(232,74,31,0.4)]'
                        }`}
                        style={{ width: asset.size, height: asset.size }}
                      >
                        <i className={`${asset.icon} text-[#E84A1F] transition-all duration-300 group-hover:scale-110 group-hover:text-[#F06030]`} style={{ fontSize: asset.size * 0.5 }}></i>
                        
                        {/* 內部光效 */}
                        <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-white/0 to-white/0 group-hover:from-white/20 group-hover:to-transparent transition-all duration-300"></div>
                        
                        {/* 連接線到中心 */}
                        <svg 
                          className="absolute pointer-events-none transition-opacity duration-300 opacity-100 group-hover:opacity-60" 
                          style={{ 
                            width: '200px', 
                            height: '200px', 
                            left: '50%', 
                            top: '50%',
                            transform: 'translate(-50%, -50%)',
                            overflow: 'visible'
                          }}
                        >
                          <line 
                            x1="0" 
                            y1="0" 
                            x2={`${(50 - asset.x) * 5}px`}
                            y2={`${(50 - asset.y) * 5}px`}
                            stroke={isDark ? 'rgba(232,74,31,0.15)' : 'rgba(232,74,31,0.1)'}
                            strokeWidth="1"
                            strokeDasharray="4 6"
                            className="animate-[dashMove_2s_linear_infinite] group-hover:stroke-[rgba(232,74,31,0.4)]"
                            style={{ transition: 'stroke 0.3s ease' }}
                          />
                        </svg>
                      </div>
                      
                      {/* 標籤 - 默認隱藏，懸停顯示 */}
                      <div 
                        className={`absolute left-1/2 -translate-x-1/2 mt-2 px-3 py-1 rounded-full text-[10px] font-semibold whitespace-nowrap transition-all duration-300 opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 ${
                          isDark 
                            ? 'bg-[#E84A1F] text-white shadow-[0_4px_15px_rgba(232,74,31,0.5)]' 
                            : 'bg-[#E84A1F] text-white shadow-[0_4px_15px_rgba(232,74,31,0.4)]'
                        }`}
                        style={{ top: '100%' }}
                      >
                        {asset.label}
                        {/* 小三角箭頭 */}
                        <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-[#E84A1F] rotate-45"></div>
                      </div>
                      
                      {/* 數據流粒子 - 從圖標流向中心 */}
                      <div 
                        className="absolute w-1.5 h-1.5 rounded-full bg-[#E84A1F] shadow-[0_0_6px_#E84A1F] group-hover:w-2 group-hover:h-2 group-hover:shadow-[0_0_12px_#E84A1F] transition-all duration-300"
                        style={{
                          left: '50%',
                          top: '50%',
                          transform: 'translate(-50%, -50%)',
                          animation: `assetDataFlow${i} ${3 + i * 0.3}s ease-in-out infinite`,
                          animationDelay: `${asset.delay + 1}s`,
                          opacity: 0
                        }}
                      ></div>
                    </div>
                  ))}
                </div>

                {/* 外層光環 */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className={`w-[90%] h-[90%] rounded-full border ${isDark ? 'border-[#E84A1F]/20' : 'border-[#E84A1F]/10'} animate-[spin_40s_linear_infinite]`}>
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-[#E84A1F] rounded-full shadow-[0_0_20px_#E84A1F]"></div>
                  </div>
                </div>
                
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className={`w-[70%] h-[70%] rounded-full border ${isDark ? 'border-[#F06030]/30' : 'border-[#F06030]/15'} animate-[spin_25s_linear_infinite_reverse]`}>
                    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-2 h-2 bg-[#F06030] rounded-full shadow-[0_0_15px_#F06030]"></div>
                  </div>
                </div>

                {/* 中心 Logo */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div 
                    className={`group relative w-32 h-32 transition-all duration-1000 cursor-pointer ${isLoaded ? 'scale-100 opacity-100' : 'scale-0 opacity-0'} ${logoClicked ? 'scale-90' : 'hover:scale-110'}`}
                    onClick={handleLogoClick}
                  >
                    {/* 发光背景 */}
                    <div className={`absolute inset-[-50%] bg-gradient-radial from-[#E84A1F]/30 via-transparent to-transparent blur-2xl transition-all duration-500 ${logoClicked ? 'from-[#E84A1F]/70 scale-150' : 'group-hover:from-[#E84A1F]/50'}`}></div>
                    
                    {/* 点击波纹效果 */}
                    {ripples.map(ripple => (
                      <div
                        key={ripple.id}
                        className="absolute inset-0 rounded-2xl pointer-events-none"
                        style={{
                          animation: 'logoRipple 1s ease-out forwards'
                        }}
                      >
                        <div className="absolute inset-[-20%] rounded-3xl border-2 border-[#E84A1F] animate-[logoRippleExpand_1s_ease-out_forwards]"></div>
                        <div className="absolute inset-[-40%] rounded-3xl border border-[#E84A1F]/60 animate-[logoRippleExpand_1s_ease-out_0.1s_forwards]"></div>
                        <div className="absolute inset-[-60%] rounded-3xl border border-[#E84A1F]/30 animate-[logoRippleExpand_1s_ease-out_0.2s_forwards]"></div>
                      </div>
                    ))}
                    
                    {/* Logo 容器 */}
                    <div className={`relative w-full h-full rounded-2xl overflow-hidden transition-all duration-500 bg-white p-4 ${logoClicked ? 'shadow-[0_0_100px_rgba(232,74,31,0.8)] rotate-[360deg]' : 'group-hover:shadow-[0_0_80px_rgba(232,74,31,0.6)]'}`}>
                      <img 
                        src="https://static.readdy.ai/image/66a2b9cfb9b417b2846085a20d5b8a97/d509b5c7881d9da6657335b51d03b561.png" 
                        alt="RoonChain Logo" 
                        className={`w-full h-full object-contain transition-all duration-500 ${logoClicked ? 'scale-125 brightness-110' : 'group-hover:scale-110'}`}
                      />
                      
                      {/* 点击闪光效果 */}
                      <div className={`absolute inset-0 bg-gradient-to-br from-white/0 via-white/0 to-white/0 transition-all duration-300 ${logoClicked ? 'from-white/60 via-white/30 to-white/0' : ''}`}></div>
                    </div>

                    {/* 脉冲环 */}
                    <div className={`absolute inset-[-10px] rounded-3xl border transition-all duration-300 ${logoClicked ? 'border-[#E84A1F] scale-125' : 'border-[#E84A1F]/50'} animate-[pulse-ring_2s_ease-out_infinite]`}></div>
                    <div className={`absolute inset-[-20px] rounded-3xl border transition-all duration-300 ${logoClicked ? 'border-[#F06030] scale-130' : 'border-[#E84A1F]/30'} animate-[pulse-ring_2s_ease-out_infinite_0.5s]`}></div>
                    
                    {/* 悬停提示 */}
                    <div className={`absolute -bottom-10 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] font-medium whitespace-nowrap transition-all duration-300 ${isDark ? 'bg-white/10 text-white/60' : 'bg-black/5 text-black/40'} ${logoClicked ? 'opacity-0 scale-90' : 'opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0'}`}>
                      Click to interact
                    </div>
                  </div>
                </div>

                {/* 浮動粒子裝飾 */}
                {[...Array(8)].map((_, index) => {
                  const angle = (index * 45 * Math.PI) / 180;
                  const distance = 45 + Math.random() * 10;
                  const x = Math.cos(angle) * distance;
                  const y = Math.sin(angle) * distance;
                  const delay = index * 0.3;
                  return (
                    <div
                      key={index}
                      className="absolute w-2 h-2 rounded-full bg-[#E84A1F] animate-[float_3s_ease-in-out_infinite]"
                      style={{
                        left: `calc(50% + ${x}%)`,
                        top: `calc(50% + ${y}%)`,
                        animationDelay: `${delay}s`,
                        boxShadow: '0 0 10px #E84A1F',
                        opacity: 0.6,
                      }}
                    ></div>
                  );
                })}

                {/* 數據流线条 */}
                <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ opacity: 0.3 }}>
                  <defs>
                    <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#E84A1F" stopOpacity="0" />
                      <stop offset="50%" stopColor="#E84A1F" stopOpacity="1" />
                      <stop offset="100%" stopColor="#E84A1F" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  {[...Array(3)].map((_, index) => {
                    const radius = 35 + index * 8;
                    return (
                      <circle
                        key={index}
                        cx="50%"
                        cy="50%"
                        r={`${radius}%`}
                        fill="none"
                        stroke="url(#lineGradient)"
                        strokeWidth="1"
                        strokeDasharray="10 20"
                        className="animate-[spin_20s_linear_infinite]"
                        style={{ animationDelay: `${index * 0.5}s` }}
                      />
                    );
                  })}
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 底部滚动提示 */}
      <div className={`absolute bottom-4 sm:bottom-6 md:bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 sm:gap-3 transition-all duration-1000 delay-1300 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}>
        <div className={`w-[1px] h-10 sm:h-12 md:h-16 bg-gradient-to-b from-transparent via-[#E84A1F]/50 to-transparent`}></div>
        <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full border flex items-center justify-center animate-bounce ${isDark ? 'border-white/20' : 'border-black/10'}`}>
          <i className={`ri-arrow-down-s-line text-sm sm:text-base ${isDark ? 'text-white/50' : 'text-black/30'}`}></i>
        </div>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-15px); }
        }
        @keyframes gradient {
          0% { background-position: 0% center; }
          100% { background-position: 200% center; }
        }
        @keyframes beam {
          0%, 100% { opacity: 0; transform: translateY(-100%); }
          50% { opacity: 1; transform: translateY(100%); }
        }
        @keyframes pulse-ring {
          0% { transform: scale(1); opacity: 0.5; }
          100% { transform: scale(1.3); opacity: 0; }
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes orbit1 {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes orbit2 {
          0% { transform: rotate(60deg); }
          100% { transform: rotate(420deg); }
        }
        @keyframes orbit3 {
          0% { transform: rotate(-45deg); }
          100% { transform: rotate(315deg); }
        }
        @keyframes nodePulse {
          0% { transform: translate(-50%, -50%) scale(1); opacity: 0.6; }
          50% { transform: translate(-50%, -50%) scale(2); opacity: 0.3; }
          100% { transform: translate(-50%, -50%) scale(3); opacity: 0; }
        }
        @keyframes nodeActivePulse {
          0% { transform: translate(-50%, -50%) scale(0.5); opacity: 1; }
          100% { transform: translate(-50%, -50%) scale(2); opacity: 0; }
        }
        @keyframes nodeActiveRing {
          0% { transform: translate(-50%, -50%) scale(0.5); opacity: 1; }
          100% { transform: translate(-50%, -50%) scale(2.5); opacity: 0; }
        }
        @keyframes chainLine {
          0% { opacity: 0; stroke-dasharray: 0 1000; }
          50% { opacity: 1; }
          100% { opacity: 0; stroke-dasharray: 1000 0; }
        }
        @keyframes chainBall {
          0% { opacity: 1; r: 6; }
          80% { opacity: 1; r: 8; }
          100% { opacity: 0; r: 4; }
        }
        @keyframes dataTransfer0 {
          0% { left: 15%; top: 25%; opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { left: 50%; top: 50%; opacity: 0; }
        }
        @keyframes dataTransfer1 {
          0% { left: 85%; top: 20%; opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { left: 50%; top: 50%; opacity: 0; }
        }
        @keyframes dataTransfer2 {
          0% { left: 10%; top: 70%; opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { left: 50%; top: 50%; opacity: 0; }
        }
        @keyframes dataTransfer3 {
          0% { left: 90%; top: 75%; opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { left: 50%; top: 50%; opacity: 0; }
        }
        @keyframes assetFloat {
          0%, 100% { transform: translate(-50%, -50%) translateY(0); }
          50% { transform: translate(-50%, -50%) translateY(-12px); }
        }
        @keyframes assetGlow {
          0%, 100% { opacity: 0.4; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.1); }
        }
        @keyframes dashMove {
          0% { stroke-dashoffset: 0; }
          100% { stroke-dashoffset: -20; }
        }
        @keyframes assetPulseRing {
          0% { transform: scale(1); opacity: 1; }
          100% { transform: scale(1.5); opacity: 0; }
        }
        @keyframes assetDataFlow0 {
          0% { transform: translate(-50%, -50%) translate(0, 0); opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { transform: translate(-50%, -50%) translate(${(50 - 8) * 3}px, ${(50 - 40) * 3}px); opacity: 0; }
        }
        @keyframes assetDataFlow1 {
          0% { transform: translate(-50%, -50%) translate(0, 0); opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { transform: translate(-50%, -50%) translate(${(50 - 92) * 3}px, ${(50 - 35) * 3}px); opacity: 0; }
        }
        @keyframes assetDataFlow2 {
          0% { transform: translate(-50%, -50%) translate(0, 0); opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { transform: translate(-50%, -50%) translate(${(50 - 12) * 3}px, ${(50 - 85) * 3}px); opacity: 0; }
        }
        @keyframes assetDataFlow3 {
          0% { transform: translate(-50%, -50%) translate(0, 0); opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { transform: translate(-50%, -50%) translate(${(50 - 88) * 3}px, ${(50 - 88) * 3}px); opacity: 0; }
        }
        @keyframes assetDataFlow4 {
          0% { transform: translate(-50%, -50%) translate(0, 0); opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { transform: translate(-50%, -50%) translate(${(50 - 5) * 3}px, ${(50 - 12) * 3}px); opacity: 0; }
        }
        @keyframes assetDataFlow5 {
          0% { transform: translate(-50%, -50%) translate(0, 0); opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { transform: translate(-50%, -50%) translate(${(50 - 95) * 3}px, ${(50 - 8) * 3}px); opacity: 0; }
        }
        @keyframes logoRipple {
          0% { transform: scale(0.8); opacity: 1; }
          100% { transform: scale(2); opacity: 0; }
        }
        @keyframes logoRippleExpand {
          0% { transform: scale(0.8); opacity: 1; }
          100% { transform: scale(2); opacity: 0; }
        }
      `}</style>
    </section>
  );
}
